import {
  Button,
  Checkbox,
  Chips,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  RightPaneLayout,
  ScrollableContent,
  TabsWithContent,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { Loading, Price, priceCell } from "src/components";
import { BidContractType, BidPackageStatus, Maybe, TakeoffLineItemVersion } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";

import { INTERNAL_ESTIMATE_ID } from "src/routes/bids/plan-based-bid-package/utils";
import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { Error } from "src/routes/Error";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { fail, isDefined, isNumber, pluralize, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { UploadBidModal } from "../plan-based-bid-package/components/UploadBidModal";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { getOptionsKey } from "./BidsTab";
import { TradeCostData, sumTradeCostData } from "./BidsTab.utils";
import {
  PlanBasedBidsTable_AggregateVersionsQuery,
  PlanBasedBidsTable_BidPackageVersionFragment,
  PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment,
  PlanBasedBidsTable_ReadyPlanFragment,
  PlanBasedBidsTable_ReadyPlanOptionFragment,
  PlanBasedBidsTable_ScopeLinesFragment,
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  usePlanBasedBidsTable_AggregateVersionsQuery,
  usePlanBasedBidsTable_ReadyPlanOptionsQuery,
  usePlanBasedBidsTable_ScopeLinesQuery,
} from "./PlanBasedBidsTable.generated";
import {
  getOptionNameCell,
  getOptionsInMultipleLocations,
  getReadyPlanOptions,
  type ReadyPlanOptionsById,
} from "./proration/PlanAndOptionTables";
import {
  getBidPackageTradesWithEstimate,
  isHeavyScopeBidPackage,
  scopeDiffStyles,
  scopeFieldHasChanged,
} from "./utils";

type PlanBasedBidsTableProps = {
  bidPackage: PageBidPackageDetailFragment;
  searchFilter?: string;
  requestsWithEditableBids: BidPackageDetailActiveRequestFragment[];
  showUncostedItemsOnly?: boolean;
};

export function PlanBasedBidsTable(props: PlanBasedBidsTableProps) {
  const { bidPackage, searchFilter, requestsWithEditableBids, showUncostedItemsOnly } = props;
  const [{ versionId }] = useQueryParams({ versionId: StringParam });

  const query = usePlanBasedBidsTable_AggregateVersionsQuery({
    variables: { bidPackageVersionId: versionId ?? bidPackage.latestVersion.id },
  });

  return queryResult(query, ({ bidPackageVersion }) =>
    bidPackageVersion.readyPlanAggregateVersions.isEmpty ? (
      <div css={Css.df.aic.jcc.p3.gray700.$}>This bid package version has no plans to bid on.</div>
    ) : (
      <PlanBasedBidsView
        bidPackage={bidPackage}
        searchFilter={searchFilter}
        requestsWithEditableBids={requestsWithEditableBids}
        bidPackageVersion={bidPackageVersion}
        showUncostedItemsOnly={showUncostedItemsOnly}
      />
    ),
  );
}

type PlanBasedBidsViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_AggregateVersionsQuery["bidPackageVersion"];
};

function PlanBasedBidsView(props: PlanBasedBidsViewProps) {
  const { bidPackageVersion, ...tableProps } = props;
  const sortedRpavs = useMemo(
    () => bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id),
    [bidPackageVersion.readyPlanAggregateVersions],
  );
  const [selectedTab, setSelectedTab] = useState<string>(sortedRpavs[0].readyPlan.id);
  const selectedReadyPlan = useMemo(() => {
    return sortedRpavs.find((rpav) => rpav.readyPlan.id === selectedTab)?.readyPlan ?? fail("ReadyPlan not found");
  }, [selectedTab, sortedRpavs]);
  const isHeavyBidPackage = useMemo(() => isHeavyScopeBidPackage(tableProps.bidPackage), [tableProps.bidPackage]);

  const [{ versionId }] = useQueryParams({ versionId: StringParam });
  const query = usePlanBasedBidsTable_ScopeLinesQuery({
    variables: {
      bidPackageVersionId: bidPackageVersion.id,
      highlightChanges:
        tableProps.bidPackage.status.code === BidPackageStatus.Draft && !isDefined(versionId) && !isHeavyBidPackage,
      filter: { readyPlan: [selectedTab] },
    },
  });
  // Only the options our scope lines actually reference — see PlanBasedBidsTable.graphql
  const optionIds = useMemo(
    () =>
      (query.data?.bidPackageVersionScopeLines ?? [])
        .flatMap((sl) => sl.takeoffLineItemVersion.options.map((o) => o.id))
        .unique(),
    [query.data?.bidPackageVersionScopeLines],
  );
  const readyPlanOptionsQuery = usePlanBasedBidsTable_ReadyPlanOptionsQuery({
    variables: { optionIds },
    skip: optionIds.isEmpty,
  });
  const readyPlanOptionsById = useMemo(
    () => readyPlanOptionsQuery.data?.readyPlanOptions.keyBy((option) => option.id) ?? {},
    [readyPlanOptionsQuery.data?.readyPlanOptions],
  );
  // The options query is keyed off ids read out of the scope lines, so it lands a beat later; without
  // this the table would render against an empty map and `getReadyPlanOptions` would fail.
  const optionsReady = optionIds.isEmpty || isDefined(readyPlanOptionsQuery.data);
  return (
    <TabsWithContent
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          // Surface option query failures directly; otherwise `readyPlanOptionsById` falls back to an
          // empty map and every option lookup blows up with a misleading "ReadyPlanOption not found".
          if (readyPlanOptionsQuery.error) return <Error error={readyPlanOptionsQuery.error} />;
          // When this tab is shown, we'll be using the `readyPlan: selectedTab` to pull back only this plan's rows
          if (query.loading || readyPlanOptionsQuery.loading || !optionsReady) return <Loading />;
          return queryResult(query, ({ bidPackageVersion, bidPackageVersionScopeLines }) => (
            <ScrollableContent virtualized>
              <RightPaneLayout>
                <div css={Css.h100.$}>
                  <PlanBasedBidsTableView
                    {...tableProps}
                    bidPackageVersion={bidPackageVersion}
                    readyPlan={selectedReadyPlan}
                    readyPlanOptionsById={readyPlanOptionsById}
                    scopeLines={bidPackageVersionScopeLines}
                    isHeavyBidPackage={isHeavyBidPackage}
                  />
                </div>
              </RightPaneLayout>
            </ScrollableContent>
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}

type PlanBasedBidsTableViewProps = PlanBasedBidsTableProps & {
  isHeavyBidPackage: boolean;
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment;
  readyPlan: PlanBasedBidsTable_ReadyPlanFragment;
  readyPlanOptionsById: ReadyPlanOptionsById;
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[];
};

function PlanBasedBidsTableView(props: PlanBasedBidsTableViewProps) {
  const {
    isHeavyBidPackage,
    bidPackage,
    searchFilter,
    requestsWithEditableBids,
    bidPackageVersion,
    readyPlan,
    readyPlanOptionsById,
    scopeLines,
    showUncostedItemsOnly = false,
  } = props;
  const { openModal } = useModal();
  const displayingOldVersion = bidPackageVersion.id !== bidPackage.latestVersion.id;
  const [onlyChanged, setOnlyChanged] = useState(false);

  const tradePartnerIdsWithRevisions = bidPackage.latestVersion.latestBidContractRevisions
    .map((bcr) => bcr.bidContract.tradePartner?.id)
    .compact();
  const requestsWithoutContracts = bidPackage.activeRequests.filter(
    (r) => !tradePartnerIdsWithRevisions.includes(r.tradePartner.id),
  );
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;
  const filteredScopeLines = useMemo(() => {
    if (!showUncostedItemsOnly) return scopeLines;
    const hasBidderCostByBidItemId = bidPackageVersion.lineItems.keyBy(
      (li) => li.bidItem.id,
      (li) => li.hasBidderCost,
    );
    return scopeLines.filter((sl) => {
      const bidItemId = sl.takeoffLineItemVersion.bidItem?.id;
      return !bidItemId || !hasBidderCostByBidItemId[bidItemId];
    });
  }, [scopeLines, showUncostedItemsOnly, bidPackageVersion.lineItems]);

  function onCreateBid() {
    if (bidPackage.isUnitBased) {
      openModal({ content: <UploadBidModal bidPackage={bidPackage} /> });
    }
  }

  const rows = useMemo(() => {
    const allRows = createRows(bidPackage, bidPackageVersion, isDraft, filteredScopeLines, readyPlanOptionsById);
    if (!onlyChanged) return allRows;
    return filterToChangedRows(allRows);
  }, [bidPackage, bidPackageVersion, filteredScopeLines, readyPlanOptionsById, isDraft, onlyChanged]);

  const columns = useMemo(() => {
    // Instead of showing all options with a location, we want to show options that share a global option in different locations or are grouped by feature
    const allOptions = filteredScopeLines
      .flatMap((scopeLine) => getReadyPlanOptions(readyPlanOptionsById, scopeLine.takeoffLineItemVersion.options))
      .unique();
    const optionsWithMultipleGlobalOptions = getOptionsInMultipleLocations(allOptions).map((o) => o.id);
    return createColumns(
      bidPackage,
      () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> }),
      isDraft,
      optionsWithMultipleGlobalOptions,
    );
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidPackage, isDraft, readyPlanOptionsById, filteredScopeLines]);

  const optionTypesGrouped = filteredScopeLines
    .flatMap((scopeLine) => getReadyPlanOptions(readyPlanOptionsById, scopeLine.takeoffLineItemVersion.options))
    .unique()
    .groupBy((o) => o.type.id);
  const optionTypesCount = Object.entries(optionTypesGrouped).map(([, options]) => ({
    optionType: options[0].type.name,
    count: options.length,
  }));

  return (
    <>
      <div css={Css.df.w100.aic.gap2.py3.p2.$}>
        <div css={Css.xl.$}>Bid Total</div>
        {<Chips values={optionTypesCount.map(({ count, optionType }) => `${count} ${pluralize(count, optionType)}`)} />}
        {isDraft && !isHeavyBidPackage && (
          <Checkbox label="Only Changed" selected={onlyChanged} onChange={setOnlyChanged} />
        )}
        {!isDraft && !displayingOldVersion && (
          <div css={Css.mla.$}>
            <Button
              variant="text"
              label={bidPackage.isUnitBased ? "Upload Costs" : "Add Costs"}
              disabled={
                (!bidPackage.isUnitBased &&
                  bidPackage.status.code === BidPackageStatus.Awarded &&
                  "Revise bid package costs to enter new pricing") ||
                ((bidPackage.isUnitBased ? requestsWithEditableBids : requestsWithoutContracts).isEmpty &&
                  "Bids cannot be uploaded")
              }
              onClick={onCreateBid}
            />
          </div>
        )}
      </div>
      <GridTable columns={columns} filter={searchFilter} rows={rows} />
    </>
  );
}

type HeaderRow = { kind: "header"; data: { costData: TradeCostData } };
type OptionRow = {
  kind: "option";
  data: { options: PlanBasedBidsTable_ReadyPlanOptionFragment[]; costData: TradeCostData };
};
type TLIVRow = {
  kind: "tliv";
  data: {
    takeoffLineItemVersion: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    changeType: Maybe<string>;
    previous: Maybe<PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment>;
    costData: TradeCostData;
  };
};

type Row = HeaderRow | OptionRow | TLIVRow;

function createRows(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[],
  readyPlanOptionsById: ReadyPlanOptionsById,
): GridDataRow<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradeIds = trades.map((r) => r.tradePartner.id);
  const itemRows = scopeLines.map(({ takeoffLineItemVersion, changeType, previous }) => ({
    kind: "tliv" as const,
    id: takeoffLineItemVersion.id,
    data: {
      takeoffLineItemVersion,
      changeType,
      previous,
      // For each materialized scope line, each trade could have bid at most once on it
      costData: trades.keyBy(
        (bpr) => bpr.tradePartner.id,
        ({ bidContractRevision, tradePartner }) => {
          // If we are in draft mode we want to pull costs from the previous version revisions
          if (isDraft) {
            const previous = bidPackageVersion.previous;
            const previousRevision =
              tradePartner.id === INTERNAL_ESTIMATE_ID
                ? previous?.latestBidContractRevisions.find(
                    (bcr) => bcr.bidContract.id === bidPackage.internalEstimateBidContract?.id,
                  )
                : previous?.latestBidContractRevisions.find(
                    (bcr) => bcr.bidContract.tradePartner?.id === tradePartner.id,
                  );
            if (bidPackage.isUnitBased) {
              const bcli = previousRevision?.lineItems.find(
                (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
              );
              // this is the unit cost on the BCLI
              return isNumber(bcli?.totalCostInCents)
                ? bcli?.totalCostInCents * getQuantity(takeoffLineItemVersion)
                : undefined;
            } else {
              /*
                Since we are looking at the previous version to show costs we need to used the identity since there may be a new TLIV
                additionally we want to make sure the options haven't changed between the versions
              */
              const revisionBcli = previousRevision?.lineItems.find(
                (li) =>
                  (li.itemTemplateItem as TakeoffLineItemVersion)?.identity.id === takeoffLineItemVersion.identity.id &&
                  takeoffLineItemVersion.options
                    .map((o) => o.id)
                    .hasSameElements(li.itemTemplateItem?.options.map((o) => o.id) ?? []),
              );
              // These are plan based tli therefore the total cost
              return revisionBcli?.totalCostInCents;
            }
          }
          // if bpr.bidcontract.type is unit based then use the bid contract line item total cost
          if (bidContractRevision?.bidContract?.type?.code === BidContractType.UnitBased) {
            const bcli = bidContractRevision.lineItems.find(
              (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
            );
            // this is the unit cost on the BCLI
            return isNumber(bcli?.totalCostInCents)
              ? bcli?.totalCostInCents * getQuantity(takeoffLineItemVersion)
              : undefined;
          }
          // for prorated bids we would want to use the itemtemplateitem cost in cents
          const revisionBcli = bidContractRevision?.lineItems.find(
            (li) => li.itemTemplateItem?.id === takeoffLineItemVersion.id,
          );
          // These are plan based tli therefore the total cost
          return revisionBcli?.totalCostInCents;
        },
      ),
    },
  }));

  const optionRows = itemRows
    .groupByObject((i) => getOptionsKey(i.data.takeoffLineItemVersion.options) || "base")
    .map(([key, children]) => {
      const { takeoffLineItemVersion } = children[0].data;
      return {
        kind: "option" as const,
        id: key,
        data: {
          options: getReadyPlanOptions(readyPlanOptionsById, takeoffLineItemVersion.options),
          costData: children
            // If we are showing removed items, don't sum up their costs
            .filter((c) => c.data.changeType !== AggregateMemberVersionChangeType.REMOVED)
            .map((c) => c.data.costData)
            .reduce(sumTradeCostData.bind(null, tradeIds), {}),
        },
        children,
      };
    });

  const headerWithCost = {
    ...simpleHeader,
    data: { costData: optionRows.map((r) => r.data.costData).reduce(sumTradeCostData.bind(null, tradeIds), {}) },
  };

  return [headerWithCost, ...optionRows];
}

function createColumns(
  bidPackage: PageBidPackageDetailFragment,
  onAddTrade: () => void,
  isDraft: boolean,
  optionsWithMultipleGlobalOptions: string[],
): GridColumn<Row>[] {
  const tradeColumns = getBidPackageTradesWithEstimate(bidPackage);
  return [
    collapseColumn<Row>({
      tliv: (data) => ({ content: emptyCell.content, css: isDraft ? scopeDiffStyles(data) : undefined }),
    }),
    column<Row>({
      header: "Code",
      option: emptyCell,
      tliv: (data) => ({
        content: <DropCodeCell scope={data.takeoffLineItemVersion} />,
        css: isDraft ? scopeDiffStyles(data, "drop") : undefined,
      }),
      w: "90px",
    }),
    column<Row>({
      header: "Name",
      option: ({ options }) => getOptionNameCell(options, optionsWithMultipleGlobalOptions),
      tliv: (data) => ({
        content: [data.takeoffLineItemVersion.name, data.takeoffLineItemVersion.bidItem?.parentMaterialVariant?.code]
          .compact()
          .join(" "),
        css: isDraft ? scopeDiffStyles(data, "materialVariant") : undefined,
      }),
    }),
    column<Row>({
      header: "Qty",
      option: emptyCell,
      tliv: (data) => ({
        content: data.takeoffLineItemVersion.unitOfMeasure.useQuantity ? data.takeoffLineItemVersion.quantity : "N/A",
        css: isDraft ? scopeDiffStyles(data, "quantity") : undefined,
      }),
      w: "53px",
    }),
    column<Row>({
      header: "UoM",
      option: emptyCell,
      tliv: (data) => ({
        content: data.takeoffLineItemVersion.unitOfMeasure.shortName,
        css: isDraft ? scopeDiffStyles(data, "unitOfMeasure") : undefined,
      }),
      w: "67px",
    }),
    column<Row>({
      header: "Location",
      option: emptyCell,
      tliv: (data) => ({
        content: data.takeoffLineItemVersion.location.displayLocationPath,
        css: isDraft ? scopeDiffStyles(data, "location") : undefined,
      }),
      w: "160px",
    }),
    ...tradeColumns.map(({ tradePartner }, idx) => {
      return numericColumn<Row>({
        // Add left border separator between trade columns (skip the first trade column)
        ...(idx > 0 ? { border: "left" as const } : {}),
        header: ({ costData }) => (
          <div css={Css.w100.df.aic.$}>
            <div css={Css.mra.$}>
              {tradePartner.name}
              {costData[tradePartner.id] && (
                <>
                  {" "}
                  (<Price valueInCents={costData[tradePartner.id]} />)
                </>
              )}
            </div>
          </div>
        ),
        option: ({ costData }) =>
          isNumber(costData[tradePartner.id]) ? priceCell({ valueInCents: costData[tradePartner.id] }) : emptyCell,
        tliv: (data) => ({
          content: isNumber(data.costData[tradePartner.id]) ? (
            <Price valueInCents={data.costData[tradePartner.id]} />
          ) : (
            emptyCell.content
          ),
          css: isDraft ? scopeDiffStyles(data) : undefined,
        }),
      });
    }),
    ...(tradeColumns.isEmpty
      ? [
          column<Row>({
            header: () => <Button label="Add Trade" variant="text" onClick={onAddTrade} />,
            option: emptyCell,
            tliv: emptyCell,
            w: "300px",
          }),
        ]
      : []),
  ];
}

type Field = keyof Pick<
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  "options" | "quantity" | "unitOfMeasure" | "materialVariant" | "drop" | "location"
>;

const allFields: Field[] = ["drop", "quantity", "unitOfMeasure", "materialVariant", "options", "location"];

function rowHasChanges(data: TLIVRow["data"]): boolean {
  if (data.changeType === AggregateMemberVersionChangeType.ADDED) return true;
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) return true;
  if (scopeFieldHasChanged(data, "options")) return true;
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED) {
    return allFields.some((f) => scopeFieldHasChanged(data, f));
  }
  return false;
}

function filterToChangedRows(rows: GridDataRow<Row>[]): GridDataRow<Row>[] {
  return rows.flatMap((row) => {
    if (row.kind === "header") {
      return [row] as GridDataRow<Row>[];
    } else if (row.kind === "option") {
      // Look down into the tlivs for changes, and only return plan/parent rows with matching children
      const filteredTlivs = (row.children ?? []).filter(
        (tlivRow) => tlivRow.kind === "tliv" && rowHasChanges(tlivRow.data),
      );
      if (filteredTlivs.length === 0) return [];
      return [{ ...row, children: filteredTlivs }];
    } else {
      return [row] as GridDataRow<Row>[];
    }
  });
}

export function getQuantity(tliv: PlanBasedBidsTable_TakeoffLineItemVersionFragment) {
  return (tliv.unitOfMeasure.useQuantity ? tliv.quantity : 1) || 1;
}
